import {t, Trans} from '@lingui/macro';
import {compact} from 'lodash';
import React, {ReactNode} from 'react';
import styled from 'styled-components';

import {
  canRegisterDefaultEmailClient,
  isElectron,
} from '../../../../libs/shared/helpers/src/environment/platformHelpers';
import {HandlerOf, Renderer, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {insert} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {UserThemesEnum} from '../../../../libs/shared/ui/src/theme/userThemesEnum';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {EmailFormatsEnum} from '../../../helpers/models/emailFormatHelpers';
import {ReplyTypesEnum} from '../../../helpers/models/recipientHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {
  CountersVisibilityModesEnum,
  DefaultSendActionsEnum,
  KeyboardShortcutsTypesEnum,
  MarkAsReadDelaysEnum,
  PreferencesModel,
  ReselectDirectionsEnum,
} from '../../../models/api/preferencesModel';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {
  getHasDefaultVideoConferencing,
  getHasGrammarlyIntegration,
  getHasInstantReschedulingLink,
  getHasP1Alphabetization,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {SettingsPreferencesDropdownItem} from '../items/settingsPreferencesDropdownItem';
import {SettingsPreferencesToggleItem} from '../items/settingsPreferencesToggleItem';
import {BadgeNamespacePreferenceControl} from '../items/teammateControls/badgeNamespacePreferenceControl';
import {ClearOfflineCacheButton} from '../items/teammateControls/buttons/clearOfflineCacheButton';
import {FrontLinksTargetButton} from '../items/teammateControls/buttons/frontLinksTargetButton';
import {MakeDefaultEmailClientButton} from '../items/teammateControls/buttons/makeDefaultEmailClientButton';
import {SignOutOtherSessionsButton} from '../items/teammateControls/buttons/signOutOtherSessionsButton';
import {DefaultCalendarDropdown} from '../items/teammateControls/defaultCalendarDropdown';
import {DefaultChannelDropdown} from '../items/teammateControls/defaultChannelDropdown';
import {DefaultPhoneNumberPrefixInput} from '../items/teammateControls/defaultPhoneNumberPrefixInput';
import {DefaultVideoConferencing} from '../items/teammateControls/defaultVideoConferencing/defaultVideoConferencing';
import {DelegationPreferenceControl} from '../items/teammateControls/delegationPreferenceControl';
import {DownloadLocationPreferenceControl} from '../items/teammateControls/downloadLocationPreferenceControl';
import {QuickResponsesDropdownContainer} from '../items/teammateControls/quickResponses/quickResponsesDropdownContainer';
import {SendLaterTimingsDropdown} from '../items/teammateControls/sendLaterTimingsDropdown';

interface RenderControlParams {
  preferences: PreferencesModel;
  patchPreferences: HandlerOf<Partial<PreferencesModel>>;
  title: ReactNode;
  description: ReactNode;
}

export interface PreferenceItem {
  renderControl: RendererOf<RenderControlParams>;
  renderTooltip?: Renderer;
  title: ReactNode;
  description: ReactNode;
  overrideDefaultRender?: boolean;
}

export interface PreferenceItemWithIndex extends PreferenceItem {
  index: number;
}

const StyledLinkDiv = styled.div`
  color: ${(p) => p.theme.palette.blue.shade40};
  display: inline-block;
  &:hover {
    color: ${(p) => p.theme.palette.blue.shade50};
  }
`;

export const useTeammatePreferences = (
  originalPreferences: PreferencesModel,
): ReadonlyArray<PreferenceItem> => {
  const localize = useLocalize();
  const userNamespace = useAppSelector(getUserNamespace);
  const hasP1Alphabetization = useAppSelector(getHasP1Alphabetization);
  const hasDefaultVideoConferencing = useAppSelector(getHasDefaultVideoConferencing);
  const hasGrammarlyIntegration = useAppSelector(getHasGrammarlyIntegration);
  const hasInstantReschedulingLink = useAppSelector(getHasInstantReschedulingLink);

  return compact([
    {
      title: localize(t('teammate-prefs-title-theme')`Theme`),
      description: localize(t('teammate-prefs-desc-theme')`Choose the appearance of Front`),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const themeValues = [UserThemesEnum.DARK, UserThemesEnum.LIGHT];
        const themeValueDisplayNames = [
          localize(t('teammate-prefs-theme-dark')`Dark`),
          localize(t('teammate-prefs-theme-light')`Light`),
        ];
        const defaultValue = UserThemesEnum.LIGHT;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.theme}
            values={themeValues}
            valueDisplayNames={themeValueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(theme) => patchPreferences({theme})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-default-send')`Send button`),
      description: localize(t('teammate-prefs-desc-default-send')`Default send button in all composers`),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [
          DefaultSendActionsEnum.SEND_AND_ARCHIVE,
          DefaultSendActionsEnum.SEND,
          DefaultSendActionsEnum.SEND_AND_SNOOZE,
        ];
        const valueDisplayNames = [
          localize(t('teammate-prefs-sendandarchive')`Send & Archive`),
          localize(t('teammate-prefs-send')`Send`),
          localize(t('teammate-prefs-sendandsnooze')`Send & Snooze`),
        ];
        const defaultValue = DefaultSendActionsEnum.SEND_AND_ARCHIVE;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.defaultSendAction}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(defaultSendAction) => patchPreferences({defaultSendAction})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-mobile-reply')`Reply button (mobile-only)`),
      description: localize(t('teammate-prefs-desc-mobile-reply')`Default reply button on all messages`),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [ReplyTypesEnum.SINGLE, ReplyTypesEnum.ALL];
        const valueDisplayNames = [
          localize(t('teammate-prefs-mobile-reply')`Reply`),
          localize(t('teammate-prefs-mobile-replyall')`Reply all`),
        ];
        const defaultValue = ReplyTypesEnum.ALL;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.defaultReplyType}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(defaultReplyType) => patchPreferences({defaultReplyType})}
          />
        );
      },
    },
    hasGrammarlyIntegration && {
      title: localize(t('teammate-prefs-title-grammarly')`Grammarly`),
      description: localize(t('teammate-prefs-desc-grarmmarly')`Enable grammar and spelling assistance`),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.grammarlyEnabled}
            updatePreference={(grammarlyEnabled) => patchPreferences({grammarlyEnabled})}
          />
        );
      },
    },
    hasInstantReschedulingLink && {
      title: localize(t('teammate-prefs-title-instant-reschedule')`Instant Rescheduling Links`),
      description: localize(
        t(
          'teammate-prefs-desc-instant-reschedule',
        )`Auto-add a rescheduling link to calendar events for easy rescheduling`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.instantReschedulingLinkEnabled}
            updatePreference={(instantReschedulingLinkEnabled) =>
              patchPreferences({instantReschedulingLinkEnabled})
            }
          />
        );
      },
    },

    {
      title: localize(t('teammate-prefs-title-reply-in-new-conversation')`Send in new conversation`),
      description: localize(
        t(
          'teammate-prefs-desc-reply-in-new-conversation',
        )`Always create a new conversation when replying, forwarding or resending`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = false;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldReplyInNewConversation}
            updatePreference={(shouldReplyInNewConversation) =>
              patchPreferences({shouldReplyInNewConversation})
            }
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-subscribe-on-comment')`Subscribe on comment`),
      description: localize(
        t(
          'teammate-prefs-desc-subscribe-on-comment',
        )`Automatically subscribe to conversations after posting a comment`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldSubscribeOnComment}
            updatePreference={(shouldSubscribeOnComment) => patchPreferences({shouldSubscribeOnComment})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-subscribe-on-reply')`Subscribe on reply`),
      description: localize(
        t(
          'teammate-prefs-desc-subscribe-on-reply',
        )`Automatically subscribe to conversations after sending a reply`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldSubscribeOnReply}
            updatePreference={(shouldSubscribeOnReply) => patchPreferences({shouldSubscribeOnReply})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-unsubscribe-on-unassign')`Unsubscribe on unassign`),
      description: localize(
        t(
          'teammate-prefs-desc-unsubscribe-on-unassign',
        )`Automatically unsubscribe from conversations you unassign or assign to a teammate`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = false;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldMuteOnUnassign}
            updatePreference={(shouldMuteOnUnassign) => patchPreferences({shouldMuteOnUnassign})}
          />
        );
      },
    },
    {
      title: localize(
        t(
          'teammate-prefs-title-unsubscribe-on-move-team-inbox',
        )`Unsubscribe on move from individual to team inbox`,
      ),
      description: localize(
        t(
          'teammate-prefs-desc-unsubscribe-on-move-team-inbox',
        )`Automatically unsubscribe from conversations you move from an individual inbox to a team inbox`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = false;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldMuteOnMoveToTeamInbox}
            updatePreference={(shouldMuteOnMoveToTeamInbox) =>
              patchPreferences({shouldMuteOnMoveToTeamInbox})
            }
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-send-on-enter-chat')`"Enter" to send in chat`),
      description: localize(
        t(
          'teammate-prefs-desc-send-on-enter-chat',
        )`Press Enter to send when writing Facebook, Twitter, Intercom, and Smooch messages`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = false;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldSendOnEnterForChat}
            updatePreference={(shouldSendOnEnterForChat) => patchPreferences({shouldSendOnEnterForChat})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-reply-in-new-window')`Reply in a new window`),
      description: localize(
        t('teammate-prefs-desc-reply-in-new-window')`Replies will be composed in a new window`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = false;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldReplyInNewWindow}
            updatePreference={(shouldReplyInNewWindow) => patchPreferences({shouldReplyInNewWindow})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-default-channel')`Default channel`),
      description: localize(
        t('teammate-prefs-desc-default-channel')`The default channel to use when composing a new message`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <DefaultChannelDropdown
          value={preferences.defaultChannelId}
          title={title}
          updatePreference={(defaultChannelId) => patchPreferences({defaultChannelId})}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-reply-with-default-channel')`Reply with default channel`),
      description: localize(
        t(
          'teammate-prefs-desc-reply-with-default-channel',
        )`Reply to conversations from the default channel, if accessible`,
      ),
      renderTooltip: () => (
        <Trans id="teammate-prefs-desc-reply-with-default-channel-tooltip">
          Only applies to shared conversations, or if both the conversation and the default channel are
          private to the same teammate.
        </Trans>
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldReplyWithDefaultChannel}
            updatePreference={(shouldReplyWithDefaultChannel) =>
              patchPreferences({shouldReplyWithDefaultChannel})
            }
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-keyboard-shortcuts')`Keyboard shortcut options`),
      description: (
        <Trans id="teammate-prefs-desc-keyboard-shortcuts">
          Select a{' '}
          <StyledLinkDiv
            onClick={() => openInBrowser('https://help.front.com/t/m2249b/front-keyboard-shortcuts')}
          >
            keyboard shortcut set
          </StyledLinkDiv>
          , or select &quot;No shortcuts&quot; to disable shortcuts
        </Trans>
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const {keepLegacyKeyboardShortcuts} = originalPreferences;
        let values = [
          KeyboardShortcutsTypesEnum.FRONT,
          KeyboardShortcutsTypesEnum.FRONT_NO_SINGLE_KEY,
          KeyboardShortcutsTypesEnum.GMAIL,
          KeyboardShortcutsTypesEnum.NONE,
        ];
        let valueDisplayNames = [
          localize(t('teammate-prefs-keyboard-shortcuts-front')`Front`),
          localize(t('teammate-prefs-keyboard-shortcuts-front-no-single')`Front (no single keys)`),
          localize(t('teammate-prefs-keyboard-shortcuts-gmail')`Gmail`),
          localize(t('teammate-prefs-keyboard-shortcuts-none')`No shortcuts`),
        ];
        let defaultValue = KeyboardShortcutsTypesEnum.FRONT;

        if (keepLegacyKeyboardShortcuts) {
          values = insert(values, 2, KeyboardShortcutsTypesEnum.LEGACY);
          valueDisplayNames = insert(
            valueDisplayNames,
            2,
            localize(t('teammate-prefs-keyboard-shortcuts-legacy')`Front (legacy settings)`),
          );
          defaultValue = KeyboardShortcutsTypesEnum.LEGACY;
        }

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.keyboardShortcutsType}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(keyboardShortcutsType) => patchPreferences({keyboardShortcutsType})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-paste-as-text')`Paste as text`),
      description: localize(
        t('teammate-prefs-desc-paste-as-text')`Strip the formatting when pasting text in a composer`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = false;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.pasteAsText}
            updatePreference={(pasteAsText) => patchPreferences({pasteAsText})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-warn-missing-attachements')`No attachments warning`),
      description: localize(
        t(
          'teammate-prefs-desc-warn-missing-attachements',
        )`Display a warning when we think a draft may be missing attachments`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldWarnMissingAttachments}
            updatePreference={(shouldWarnMissingAttachments) =>
              patchPreferences({shouldWarnMissingAttachments})
            }
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-warn-many-recipients')`Many recipients warning`),
      description: localize(
        t(
          'teammate-prefs-desc-warn-many-recipients',
        )`Always confirm before messaging more than 10 recipients`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldWarnManyRecipients}
            updatePreference={(shouldWarnManyRecipients) => patchPreferences({shouldWarnManyRecipients})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-selection-direction')`Selection direction`),
      description: localize(
        t('teammate-prefs-desc-selection-direction')`When the current conversation disappears, go up or down`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [ReselectDirectionsEnum.SMART, ReselectDirectionsEnum.UP, ReselectDirectionsEnum.DOWN];
        const valueDisplayNames = [
          localize(t('teammate-prefs-selection-direction-smart')`Smart`),
          localize(t('teammate-prefs-selection-direction-up')`Up`),
          localize(t('teammate-prefs-selection-direction-down')`Down`),
        ];
        const defaultValue = ReselectDirectionsEnum.SMART;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.reselectDirection}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(reselectDirection) => patchPreferences({reselectDirection})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-print-with-attachments')`Print attachments`),
      description: localize(
        t('teammate-prefs-desc-print-with-attachments')`Include the attachments when printing a message`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldPrintWithAttachments}
            updatePreference={(shouldPrintWithAttachments) => patchPreferences({shouldPrintWithAttachments})}
          />
        );
      },
    },
    hasP1Alphabetization && {
      title: localize(
        t('teammate-prefs-title-sort-workspace-lists-alphabetically')`Sort workspace lists alphabetically`,
      ),
      description: localize(
        t(
          'teammate-prefs-desc-sort-workspace-lists-alphabetically',
        )`Display tags and team inboxes in alphabetical order in your workspace`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldAlphabetizeWorkspace}
            updatePreference={(shouldAlphabetizeWorkspace) => patchPreferences({shouldAlphabetizeWorkspace})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-hide-external-images')`Hide external images`),
      description: localize(
        t('teammate-prefs-desc-hide-external-images')`Require confirmation to display images in emails.`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = false;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldHideExternalImages}
            updatePreference={(shouldHideExternalImages) => patchPreferences({shouldHideExternalImages})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-time-format')`Long time format`),
      description: localize(
        t('teammate-prefs-desc-time-format')`Click on any timestamp to toggle between long/short formats`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [TimeFormatsEnum.H12, TimeFormatsEnum.H24];
        const valueDisplayNames = [
          localize(t('teammate-prefs-time-format-h12')`12-hour clock`),
          localize(t('teammate-prefs-time-format-h24')`24-hour clock`),
        ];
        const defaultValue = TimeFormatsEnum.H12;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.timeFormat}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(timeFormat) => patchPreferences({timeFormat})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-send-later')`Send later timings`),
      description: localize(t('teammate-prefs-desc-send-later')`Send Later options available in the app`),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <SendLaterTimingsDropdown
          selectedTimings={preferences.sendLaterValues}
          title={title}
          updatePreference={(sendLaterValues) => patchPreferences({sendLaterValues})}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-email-format')`Email format`),
      description: localize(t('teammate-prefs-desc-email-format')`Email composing format`),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [EmailFormatsEnum.HTML, EmailFormatsEnum.MARKDOWN];
        const valueDisplayNames = [
          localize(t('teammate-prefs-email-format-html')`HTML`),
          localize(t('teammate-prefs-email-format-markdown')`Markdown`),
        ];
        const defaultValue = EmailFormatsEnum.HTML;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.lastEmailFormat}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(lastEmailFormat) => patchPreferences({lastEmailFormat})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-quick-response-1')`Message template #1`),
      description: localize(
        t('teammate-prefs-desc-quick-response-1')`Assign a message template to shortcut cmd/ctrl+shift+5`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <QuickResponsesDropdownContainer
          value={preferences.quickReponsesIds[0]}
          title={title}
          updatePreference={(quickResponsesId) => {
            const quickReponsesIds = [...preferences.quickReponsesIds];
            quickReponsesIds[0] = quickResponsesId;
            patchPreferences({quickReponsesIds});
          }}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-quick-response-2')`Message template #2`),
      description: localize(
        t('teammate-prefs-desc-quick-response-2')`Assign a message template to shortcut cmd/ctrl+shift+6`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <QuickResponsesDropdownContainer
          value={preferences.quickReponsesIds[1]}
          title={title}
          updatePreference={(quickResponsesId) => {
            const quickReponsesIds = [...preferences.quickReponsesIds];
            quickReponsesIds[1] = quickResponsesId;
            patchPreferences({quickReponsesIds});
          }}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-quick-response-3')`Message template #3`),
      description: localize(
        t('teammate-prefs-desc-quick-response-3')`Assign a message template to shortcut cmd/ctrl+shift+7`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <QuickResponsesDropdownContainer
          value={preferences.quickReponsesIds[2]}
          title={title}
          updatePreference={(quickResponsesId) => {
            const quickReponsesIds = [...preferences.quickReponsesIds];
            quickReponsesIds[2] = quickResponsesId;
            patchPreferences({quickReponsesIds});
          }}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-quick-response-4')`Message template #4`),
      description: localize(
        t('teammate-prefs-desc-quick-response-4')`Assign a message template to shortcut cmd/ctrl+shift+8`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <QuickResponsesDropdownContainer
          value={preferences.quickReponsesIds[3]}
          title={title}
          updatePreference={(quickResponsesId) => {
            const quickReponsesIds = [...preferences.quickReponsesIds];
            quickReponsesIds[3] = quickResponsesId;
            patchPreferences({quickReponsesIds});
          }}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-quick-response-5')`Message template #5`),
      description: localize(
        t('teammate-prefs-desc-quick-response-5')`Assign a message template to shortcut cmd/ctrl+shift+9`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <QuickResponsesDropdownContainer
          value={preferences.quickReponsesIds[4]}
          title={title}
          updatePreference={(quickResponsesId) => {
            const quickReponsesIds = [...preferences.quickReponsesIds];
            quickReponsesIds[4] = quickResponsesId;
            patchPreferences({quickReponsesIds});
          }}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-mark-as-read')`Mark as read`),
      description: localize(
        t('teammate-prefs-desc-mark-as-read')`Choose when messages are automatically marked as read`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [
          MarkAsReadDelaysEnum.NEVER,
          MarkAsReadDelaysEnum.NO_DELAY_ON_SELECTION,
          MarkAsReadDelaysEnum.ON_MESSAGE_OPEN,
          MarkAsReadDelaysEnum.ONE_SECOND,
          MarkAsReadDelaysEnum.TWO_SECONDS,
          MarkAsReadDelaysEnum.FIVE_SECONDS,
        ];
        const valueDisplayNames = [
          localize(t('teammate-prefs-dont-mark')`Don't mark automatically`),
          localize(t('teammate-prefs-mark-on-selection')`Immediately on selection`),
          localize(t('teammate-prefs-mark-on-message-open')`When message is opened`),
          localize(t('teammate-prefs-mark-after-one-second')`After 1 second delay`),
          localize(t('teammate-prefs-mark-after-two-seconds')`After 2 second delay`),
          localize(t('teammate-prefs-mark-after-five-seconds')`After 5 second delay`),
        ];
        const defaultValue = MarkAsReadDelaysEnum.ON_MESSAGE_OPEN;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.markAsReadDelay}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(markAsReadDelay) => patchPreferences({markAsReadDelay})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-default-calendar')`Default calendar`),
      description: localize(
        t('teammate-prefs-desc-default-calendar')`The default calendar to use when creating a new event`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <DefaultCalendarDropdown
          currentCalendarVersionId={preferences.defaultCalendarVersionId}
          title={title}
          updatePreference={(defaultCalendarVersionId) => patchPreferences({defaultCalendarVersionId})}
        />
      ),
    },
    hasDefaultVideoConferencing && {
      title: localize(t('teammate-prefs-title-default-video-conference')`Default video conference`),
      description: localize(
        t(
          'teammate-prefs-desc-default-video-conference',
        )`Specify the video conference to add to all new events you create on a calendar. Different calendars can have different defaults.`,
      ),
      overrideDefaultRender: true,
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <DefaultVideoConferencing
          defaultVideoConferences={preferences.defaultVideoConferencing}
          title={title}
          description={description}
          updatePreference={(defaultVideoConferencing) =>
            patchPreferences({
              defaultVideoConferencing,
            })
          }
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-calendar-week-start')`Week start`),
      description: localize(t('teammate-prefs-desc-calendar-week-start')`Show the first day of the week as:`),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [
          CalendarWeekDaysEnum.SUNDAY,
          CalendarWeekDaysEnum.MONDAY,
          CalendarWeekDaysEnum.TUESDAY,
          CalendarWeekDaysEnum.WEDNESDAY,
          CalendarWeekDaysEnum.THURSDAY,
          CalendarWeekDaysEnum.FRIDAY,
          CalendarWeekDaysEnum.SATURDAY,
        ];
        const valueDisplayNames = [
          localize(t('teammate-prefs-week-start-sun')`Sunday`),
          localize(t('teammate-prefs-week-start-mon')`Monday`),
          localize(t('teammate-prefs-week-start-tues')`Tuesday`),
          localize(t('teammate-prefs-week-start-wed')`Wednesday`),
          localize(t('teammate-prefs-week-start-thurs')`Thursday`),
          localize(t('teammate-prefs-week-start-fri')`Friday`),
          localize(t('teammate-prefs-week-start-sat')`Saturday`),
        ];
        const defaultValue = CalendarWeekDaysEnum.SUNDAY;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.calendarWeekStartDay}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(calendarWeekStartDay) => patchPreferences({calendarWeekStartDay})}
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-send-usage-data')`Send usage data`),
      description: localize(
        t('teammate-prefs-desc-send-usage-data')`Send data to help us improving the performance of the app`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = true;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldSendUsageData}
            updatePreference={(shouldSendUsageData) => patchPreferences({shouldSendUsageData})}
          />
        );
      },
    },
    {
      title: localize(
        t('teammate-prefs-title-public-api-access')`Allow access to my individual resources via the API`,
      ),
      description: localize(
        t(
          'teammate-prefs-desc-public-api-access',
        )`Allow your channels, contacts, conversations, inboxes, messages, rules and tags to be accessible via the API.`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const defaultValue = false;

        return (
          <SettingsPreferencesToggleItem
            title={title}
            description={description}
            defaultValue={defaultValue}
            value={preferences.shouldEnablePublicApiAccess}
            updatePreference={(shouldEnablePublicApiAccess) =>
              patchPreferences({shouldEnablePublicApiAccess})
            }
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-badge-counter')`Badge counter`),
      description: localize(
        t('teammate-prefs-desc-badge-counter')`Manage the counter reflected in your badge`,
      ),
      renderControl: ({preferences, patchPreferences}) => (
        <BadgeNamespacePreferenceControl
          namespace={preferences.badgeNamespace}
          onNamespaceChange={(badgeNamespace) => patchPreferences({badgeNamespace})}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-phone-number-prefix')`Default phone number prefix`),
      description: localize(
        t(
          'teammate-prefs-desc-phone-number-prefix',
        )`Used when inputting phone numbers without prefixes in the composer and contact manager`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => (
        <DefaultPhoneNumberPrefixInput
          value={preferences.defaultNumberPrefix}
          title={title}
          updatePreference={(defaultNumberPrefix) => patchPreferences({defaultNumberPrefix})}
        />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-team-inbox-tabs')`Team inbox tabs`),
      description: localize(
        t(
          'teammate-prefs-desc-team-inbox-tabs',
        )`Choose between "Unassigned" and "Assigned" or a combined "Open" tab`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [
          false, // "team",
          true, // "teammate"
        ];
        const valueDisplayNames = [
          localize(t('teammate-prefs-team-inbox-tabs-team')`Unassigned / Assigned`),
          localize(t('teammate-prefs-team-inbox-tabs-teammate')`Open`),
        ];
        const defaultValue = false;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.shouldUseOpenFilterForTeams}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(shouldUseOpenFilterForTeams) =>
              patchPreferences({shouldUseOpenFilterForTeams})
            }
          />
        );
      },
    },
    isElectron() && {
      title: localize(t('teammate-prefs-title-download-location')`Download location`),
      description: localize(t('teammate-prefs-desc-download-location')`Current: `),
      overrideDefaultRender: true,
      renderControl: ({description, title}) => (
        <DownloadLocationPreferenceControl title={title} description={description} />
      ),
    },
    {
      title: localize(t('teammate-prefs-title-front-links-target')`Front links target`),
      description: localize(
        t(
          'teammate-prefs-desc-front-links-target',
        )`Decide if Front links open in your browser or desktop application`,
      ),
      renderControl: () => <FrontLinksTargetButton />,
    },
    canRegisterDefaultEmailClient() && {
      title: localize(t('teammate-prefs-title-default-email-client')`Default email client`),
      description: localize(
        t('teammate-prefs-desc-default-email-client')`Set Front as your system-wide email client`,
      ),
      renderControl: () => <MakeDefaultEmailClientButton />,
    },
    {
      title: localize(t('teammate-prefs-title-other-sessions')`Other sessions`),
      description: localize(
        t(
          'teammate-prefs-desc-other-sessions',
        )`Use this to ensure that this account is no longer accessible from any other device`,
      ),
      renderControl: () => <SignOutOtherSessionsButton />,
    },
    {
      title: localize(t('teammate-prefs-title-offline-cache')`Offline cache`),
      description: localize(
        t(
          'teammate-prefs-desc-offline-cache',
        )`Reclaim disk space by deleting recent conversations from the Front cache`,
      ),
      renderControl: () => <ClearOfflineCacheButton />,
    },
    {
      title: localize(t('teammate-prefs-title-inbox-counters')`Inbox counters`),
      description: localize(
        t('teammate-prefs-desc-inbox-counters')`Set the value of the counters for your individual inboxes`,
      ),
      renderControl: ({preferences, patchPreferences, title, description}) => {
        const values = [CountersVisibilityModesEnum.UNREAD, CountersVisibilityModesEnum.OPEN];
        const valueDisplayNames = [
          localize(t('teammate-prefs-counter-visibility-unread')`Unread conversations`),
          localize(t('teammate-prefs-counter-visibility-open')`Open conversations`),
        ];
        const defaultValue = CountersVisibilityModesEnum.OPEN;

        return (
          <SettingsPreferencesDropdownItem
            title={title}
            description={description}
            value={preferences.countersVisibilities[userNamespace]}
            values={values}
            valueDisplayNames={valueDisplayNames}
            defaultValue={defaultValue}
            updatePreference={(newVisibility) =>
              patchPreferences({
                countersVisibilities: {...preferences.countersVisibilities, [userNamespace]: newVisibility},
              })
            }
          />
        );
      },
    },
    {
      title: localize(t('teammate-prefs-title-delegated-inboxes')`Delegated inboxes`),
      description: localize(
        t(
          'teammate-prefs-desc-delegated-inboxes',
        )`Select the delegated inboxes you want to show in your sidebar`,
      ),
      renderControl: ({preferences, patchPreferences}) => (
        <DelegationPreferenceControl patchPreferences={patchPreferences} preferences={preferences} />
      ),
    },
  ]);
};
